import PropTypes from 'prop-types';
import { Button } from '@progress/kendo-react-buttons';
import { Field, Form, FormElement } from '@progress/kendo-react-form';
import {
  FormCheckbox,
  FormDatePicker,
  FormDropDownList,
  FormInput,
  FormMultiSelect,
  FormTextArea,
} from 'commons/forms/form';
import { required, requiredArray, requiredDateGreater, requiredDateLower } from 'commons/forms/validators';
import CustomModal from 'commons/modal/CustomModal';
import * as dates from 'commons/models/dates';
import React, { useMemo, useState } from 'react';
import 'scss/_modal.scss';
import { useGetBannieresQuery, useGetCampagneTypesQuery, useGetMagasinsQuery } from '../../../../redux/api/cache';

export default function CreateProjectModal(props) {
  const [dateDebut, setDateDebut] = useState(null);
  const [dateFin, setDateFin] = useState(null);
  const { data: projectTypeDropdown } = useGetCampagneTypesQuery();
  const { data: storesDropdown } = useGetMagasinsQuery();
  const { data: banners } = useGetBannieresQuery();

  const formatDate = (date) => {
    return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
  };

  const getStoresValues = (values) => {
    if (
      storesDropdown?.data?.length > 0 &&
      values !== undefined &&
      values !== ''
    ) {
      let stores = values
        .split(',')
        ?.map(
          (mag) =>
            storesDropdown?.data?.find((store) => String(store.mag_num) === mag)
              ?.mag_nom
        );
      if (stores === undefined) return '';
      else return stores.filter((store) => store !== undefined);
    }
  };

  const handleSubmit = (dataItem) => {
    let banner = banners?.data?.find(
      (item) => item.maggrp_descr === dataItem.banner
    )?.maggrp_cd;

    let types = projectTypeDropdown?.data?.find(
      (type) => type.camptyp_descr === dataItem.projectType
    ).camptyp_cd;

    let stores = dataItem.stores
      ?.map(
        (setStore) =>
          storesDropdown?.data?.find((store) => store.mag_nom === setStore)
            .mag_num
      )
      .join(',');

    let dDebut = formatDate(dataItem.dDebut);
    let dFin = formatDate(dataItem.dFin);
    props.create({
      camp_num: props.project?.camp_num,
      camp_descr: dataItem.projectName,
      camptyp_cd: types,
      mag_num_lst: stores,
      maggrp_cd: banner,
      camp_dt_deb: dDebut,
      camp_dt_fin: dFin,
      camp_note: dataItem.note,
      camp_seq: props.project?.camp_seq,
      camp_confirme: dataItem.confirmed,
      camp_seq_parent:
        props.parentproject === undefined
          ? props.project?.camp_seq_parent
          : props.parentproject.camp_seq,
    });
    props.toggleDialog();
  };

  const projectNameValidator = (e) => required(e, 'Nom du projet');
  const banniereValidator = (e) => required(e, 'Bannière');
  const projectTypeValidator = (e) => requiredArray(e, 'Type de projet');
  const dateDebutValidator = useMemo(() => [(e) => required(e, 'Date de début'), (e) => requiredDateLower(e, dateFin, "La date de début", "la date de fin"), (e) => props.parentproject && props.project === undefined ? requiredDateGreater(e, dates.dateBdToDate(props.parentproject.camp_dt_deb, '00:00'), "La date de début du sous-projet", "la date de début du projet parent") : ""], [dateFin]);
  const dateFinValidator = useMemo(() => [(e) => required(e, 'Date de fin'), (e) => requiredDateGreater(e, dateDebut, "La date de fin", "la date de début"), (e) => props.parentproject && props.project === undefined ? requiredDateLower(e, dates.dateBdToDate(props.parentproject.camp_dt_fin, '00:00'), "La date de fin du sous-projet", "la date de fin du projet parent") : ""], [dateDebut]);


  return (
    <div>
      <CustomModal
        title={
          props.project === undefined ? 'AJOUTER PROJET' : 'MODIFIER UN PROJET'
        }
        onClose={props.toggleDialog}
        body={
          <Form
            initialValues={{
              banner: props.project?.maggrp_cd,
              projectName: props.project?.camp_descr,
              projectType: projectTypeDropdown?.data?.find(
                (item) => item.camptyp_cd === props.project?.camptyp_cd
              )?.camptyp_descr,
              stores: getStoresValues(props.project?.mag_num_lst),
              dDebut:
                props.project !== undefined
                  ? dates.addDays(props.project?.camp_dt_deb, 1)
                  : null,
              dFin:
                props.project !== undefined
                  ? dates.addDays(props.project?.camp_dt_fin, 1)
                  : null,
              note: props.project?.camp_note,
              confirmed:
                props.project?.camp_confirme === undefined
                  ? null
                  : props.project?.camp_confirme,
            }}
            onSubmit={handleSubmit}
            render={(formRenderProps) => (
              <FormElement>
                <fieldset className={'k-form-fieldset'}>
                  {props.parentproject !== undefined && (
                    <legend className={'k-form-legend'}>
                      {`PROJET PARENT: ${props.parentproject.camp_descr}`}
                    </legend>
                  )}
                  <div className="row">
                    <div className="col-md-12">
                      <Field
                        id={'projectName'}
                        name={'projectName'}
                        label={'Nom du projet'}
                        component={FormInput}
                        validator={projectNameValidator}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <Field
                        id={'banner'}
                        name={'banner'}
                        label={'Bannière'}
                        component={FormDropDownList}
                        data={banners?.data?.map((item) => item.maggrp_descr)}
                        validator={banniereValidator}
                      />
                    </div>
                    <div className="col-md-6">
                      <Field
                        id={'projectType'}
                        name={'projectType'}
                        label={'Type de projet'}
                        component={FormDropDownList}
                        validator={projectTypeValidator}
                        data={projectTypeDropdown?.data?.map((item) =>
                          String(item.camptyp_descr)
                        )}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-4">
                      <Field
                        id={'dDebut'}
                        name={'dDebut'}
                        label={'Date de début'}
                        onChange={(e) => { setDateDebut(e.value); }}
                        component={FormDatePicker}
                        validator={dateDebutValidator}
                        wrapperStyle={{
                          minWidth: '10rem',
                        }}
                      />
                    </div>
                    <div className="col-md-4">
                      <Field
                        id={'dFin'}
                        name={'dFin'}
                        label={'Date de fin'}
                        onChange={(e) => { setDateFin(e.value); }}
                        component={FormDatePicker}
                        validator={dateFinValidator}
                        wrapperStyle={{
                          minWidth: '10rem',
                        }}
                      />
                    </div>
                    <div className="col-md-1">
                      <Field
                        id={'confirmed'}
                        name={'confirmed'}
                        label={`Confirmé`}
                        disabled={props.project?.camp_confirme === true}
                        component={FormCheckbox}
                        wrapperStyle={{
                          minWidth: '10rem',
                        }}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-12">
                      <Field
                        id={'stores'}
                        name={'stores'}
                        label={'Magasins'}
                        optional={true}
                        component={FormMultiSelect}
                        data={storesDropdown?.data?.map((item) => item.mag_nom)}
                      />
                    </div>
                  </div>
                  <Field
                    id={'note'}
                    name={'note'}
                    label={'Notes'}
                    optional={true}
                    component={FormTextArea}
                  />
                  <span className={'k-form-separator'} />
                  <div className="k-form-buttons">
                    <Button
                      type={'submit'}
                      className="blueButton"
                      disabled={!formRenderProps.allowSubmit}
                    >
                      Enregistrer
                    </Button>
                    <Button
                      onClick={formRenderProps.onFormReset}
                      className="whiteButton"
                      style={{ width: '100px', height: '36px' }}
                    >
                      Réinitialiser
                    </Button>
                  </div>
                </fieldset>
              </FormElement>
            )}
          />
        }
      />
    </div>
  );
}

CreateProjectModal.propTypes = {
  create: PropTypes.func,
  parentproject: PropTypes.shape({
    camp_descr: PropTypes.any,
    camp_seq: PropTypes.any,
    camp_dt_deb: PropTypes.any,
    camp_dt_fin: PropTypes.any
  }),
  project: PropTypes.shape({
    camp_confirme: PropTypes.any,
    camp_descr: PropTypes.any,
    camp_dt_deb: PropTypes.any,
    camp_dt_fin: PropTypes.any,
    camp_seq: PropTypes.any,
    camp_seq_parent: PropTypes.any,
    camptyp_cd: PropTypes.any,
    mag_num_lst: PropTypes.any,
    note: PropTypes.any,
    camp_num: PropTypes.any,
    maggrp_cd: PropTypes.any,
    camp_note: PropTypes.any
  }),
  toggleDialog: PropTypes.func,
};
